/** What the company Overview asks for attention on. Pure for testing. */
import { missingRates } from "./billingModel";
import type {
  Company,
  CompanyBilling,
  CompanyCaseRow,
  FirmCaseRequest,
  PlaybookRule,
  ProposedValue
} from "./types";
import { daysUntil, formatDate, withArticle } from "./utils";

export type AttentionItem =
  | {
      kind: "proposal";
      id: string;
      tone: "primary";
      title: string;
      sub: string;
      proposal: ProposedValue;
    }
  | {
      kind: "deadline";
      id: string;
      tone: "red" | "amber";
      title: string;
      sub: string;
      row: CompanyCaseRow;
    }
  | {
      kind: "playbook";
      id: string;
      tone: "primary";
      title: string;
      sub: string;
      rule: PlaybookRule;
    }
  | {
      kind: "profile";
      id: string;
      tone: "amber";
      title: string;
      sub: string;
      /** The profile sheet field to open on. */
      field: string;
    }
  | {
      kind: "request";
      id: string;
      tone: "amber";
      title: string;
      sub: string;
      requestId: string;
    }
  | { kind: "jobs"; id: string; tone: "amber"; title: string; sub: string }
  | { kind: "rates"; id: string; tone: "amber"; title: string; sub: string };

export const attentionItems = (
  company: Company,
  cases: CompanyCaseRow[],
  now: Date = new Date(),
  billing?: Pick<CompanyBilling, "approved_rates" | "timekeepers"> | null,
  requests: FirmCaseRequest[] = []
): AttentionItem[] => {
  const items: AttentionItem[] = [];
  const byId = new Map(cases.map((c) => [c.individual_id, c]));
  const entityName = (id: string | null) =>
    company.entities.find((e) => e.id === id)?.legal_name ?? company.name;

  requests
    .filter((r) => r.status === "pending_firm")
    .forEach((r) => {
      const approver =
        r.approved_by && r.approved_by.id !== r.requested_by.id
          ? ` Approved by ${r.approved_by.name}.`
          : "";
      items.push({
        kind: "request",
        id: `request-${r.id}`,
        tone: "amber",
        title: `${r.requested_by.name} asked for ${withArticle(
          r.case_type
        )} for ${r.employee_name}`,
        sub: `${
          r.created_at ? `Sent ${formatDate(r.created_at)}.` : ""
        }${approver} Accept it to open the case and send the questionnaire.`.trim(),
        requestId: r.id
      });
    });

  cases
    .filter(
      (c) =>
        c.open &&
        c.next_deadline &&
        daysUntil(c.next_deadline.due_date, now) <= 30
    )
    .sort((a, b) =>
      (a.next_deadline?.due_date ?? "").localeCompare(
        b.next_deadline?.due_date ?? ""
      )
    )
    .forEach((c) => {
      const d = c.next_deadline as NonNullable<CompanyCaseRow["next_deadline"]>;
      const days = daysUntil(d.due_date, now);
      items.push({
        kind: "deadline",
        id: `deadline-${c.individual_id}`,
        tone: days <= 7 ? "red" : "amber",
        title: `${c.name}: ${d.title}`,
        sub: `Due ${formatDate(d.due_date)}, in ${days} ${
          days === 1 ? "day" : "days"
        }.`,
        row: c
      });
    });

  company.proposals
    .filter((p) => p.field === "fein")
    .forEach((p) => {
      const from = p.individual_id ? byId.get(p.individual_id)?.name : null;
      items.push({
        kind: "proposal",
        id: `proposal-${p.id}`,
        tone: "primary",
        title: `FEIN ${p.value} for ${entityName(p.entity_id)}`,
        sub: `Found in ${
          from ? `${from}'s` : "a case's"
        } employer documents. Saving it fills the petitioner FEIN on every case that uses this entity.`,
        proposal: p
      });
    });

  (company.playbook ?? [])
    .filter((r) => r.status === "suggested")
    .forEach((r) => {
      items.push({
        kind: "playbook",
        id: `playbook-${r.id}`,
        tone: "primary",
        title: "Suggested playbook rule",
        sub: r.source ? `${r.text} (${r.source})` : r.text,
        rule: r
      });
    });

  company.entities.forEach((e, i) => {
    const suggested = company.proposals.some(
      (p) => p.field === "fein" && p.entity_id === e.id
    );
    const noFein = !e.fein && !suggested;
    const missing = [
      noFein && "FEIN",
      !e.signatory?.name && "authorized signatory"
    ].filter(Boolean);
    if (missing.length) {
      items.push({
        kind: "profile",
        id: `profile-${e.id}`,
        tone: "amber",
        title: `${e.legal_name} has no ${missing.join(" or ")}`,
        sub: "Forms leave the petitioner fields blank until it's on the company record.",
        field: noFein ? `ent-${i}-fein` : `ent-${i}-sig-name`
      });
    }
  });

  const noJob = cases.filter(
    (c) => c.open && c.linked && !c.job_description_id
  );
  if (noJob.length && company.job_descriptions.length) {
    items.push({
      kind: "jobs",
      id: "jobs",
      tone: "amber",
      title: `${noJob.length} open ${
        noJob.length === 1 ? "case has" : "cases have"
      } no job description`,
      sub: "Pick one so support letters and the LCA use the right title, SOC code and wage level."
    });
  }

  const noRate = billing ? missingRates(billing) : 0;
  if (noRate) {
    items.push({
      kind: "rates",
      id: "rates",
      tone: "amber",
      title: `${noRate} ${
        noRate === 1 ? "timekeeper has" : "timekeepers have"
      } no approved rate`,
      sub: `${company.name}'s e-billing rejects their lines, so the export leaves them out until the rate is approved.`
    });
  }
  return items;
};
